import { useEffect, useRef, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import {
  applyDagreLayout,
  DagreLayoutOptions,
  DAGRE_LAYOUT_PRESETS,
  DagreLayoutPreset,
} from '../utils/workflowDagreLayout';

const PRESET_STORAGE_KEY = 'workflow_dagre_preset';

interface UsePostMutationLayoutOptions extends DagreLayoutOptions {
  fitViewDuration?: number;
  fitViewPadding?: number;
  preset?: DagreLayoutPreset;
  onBeforeLayout?: (nodes: Node[]) => void; // snapshot hook
}

/**
 * Hook that triggers a full dagre layout pass whenever a chat-with-canvas
 * mutation lands in React state — with zero timers.
 *
 * Also exposes `relayout(preset?)` for the manual Re-layout button.
 *
 * Usage:
 *   const { triggerLayout, relayout, isLayoutPending } =
 *     usePostMutationLayout(nodes, edges, setNodes, fitView);
 *
 *   // after chat mutation:
 *   triggerLayout();
 *
 *   // from Re-layout button:
 *   relayout('spacious');
 */
export function usePostMutationLayout(
  nodes: Node[],
  edges: Edge[],
  setNodes: (updater: Node[] | ((nds: Node[]) => Node[])) => void,
  fitView: (options?: any) => void,
  options: UsePostMutationLayoutOptions = {}
): {
  triggerLayout: () => void;
  relayout: (preset?: DagreLayoutPreset) => void;
  isLayoutPending: () => boolean;
} {
  const {
    direction       = 'TB',
    fitViewDuration = 800,
    fitViewPadding  = 0.25,
    preset: initialPreset,
    onBeforeLayout,
  } = options;

  // Active preset — initialised from prop, then localStorage, then default
  const activePresetRef = useRef<DagreLayoutPreset>(
    initialPreset ??
    (localStorage.getItem(PRESET_STORAGE_KEY) as DagreLayoutPreset | null) ??
    'comfortable'
  );

  // Flag: a mutation has landed and we need to layout on the next nodes update
  const isMutationPending = useRef(false);

  // Track the node IDs from the previous render so we can detect when
  // replace_workflow has actually flushed through React state
  const prevNodeIdsRef = useRef<string>('');

  // ── Core layout runner ────────────────────────────────────────────────────
  const runLayout = useCallback((
    currentNodes: Node[],
    currentEdges: Edge[],
    preset: DagreLayoutPreset
  ) => {
    const { rankSep, nodeSep, edgeSep } = DAGRE_LAYOUT_PRESETS[preset];

    console.log('🗺️ DAGRE-LAYOUT: Running with preset', preset, { rankSep, nodeSep, edgeSep });

    // Snapshot before mutating positions
    onBeforeLayout?.(currentNodes);

    const laidOutNodes = applyDagreLayout(currentNodes, currentEdges, {
      direction,
      rankSep,
      nodeSep,
      edgeSep,
    });

    setNodes(laidOutNodes);

    // Wait for the CSS transition (600ms) to finish before fitting view
    // so fitView measures final node positions, not mid-animation ones
    const TRANSITION_MS = 620;
    window.setTimeout(() => {
      fitView({ padding: fitViewPadding, duration: fitViewDuration });
      console.log('🔔 POST-MUTATION-LAYOUT: fitView called after transition settled');
    }, TRANSITION_MS);
  }, [direction, fitView, fitViewDuration, fitViewPadding, setNodes]);

  // ── triggerLayout — called after chat mutation, waits for state flush ─────
  const triggerLayout = useCallback(() => {
    console.log('🔔 POST-MUTATION-LAYOUT: triggerLayout called — waiting for nodes to update');
    isMutationPending.current = true;
  }, []);

  // ── relayout — called imperatively from the Re-layout button ─────────────
  const relayout = useCallback((preset?: DagreLayoutPreset) => {
    const resolvedPreset = preset ?? activePresetRef.current;
    activePresetRef.current = resolvedPreset;
    localStorage.setItem(PRESET_STORAGE_KEY, resolvedPreset);

    console.log('🔔 RELAYOUT: Manual relayout triggered with preset', resolvedPreset);

    setNodes(currentNodes => {
      // Snapshot before layout
      onBeforeLayout?.(currentNodes);

      const { rankSep, nodeSep, edgeSep } = DAGRE_LAYOUT_PRESETS[resolvedPreset];
      const laidOutNodes = applyDagreLayout(currentNodes, edges, {
        direction,
        rankSep,
        nodeSep,
        edgeSep,
      });

      // Wait for CSS transition to finish before fitting view
      window.setTimeout(() => {
        fitView({ padding: fitViewPadding, duration: fitViewDuration });
        console.log('🔔 RELAYOUT: fitView called after transition settled');
      }, 620);

      return laidOutNodes;
    });
  }, [direction, edges, fitView, fitViewDuration, fitViewPadding, onBeforeLayout, setNodes]);

  const isLayoutPending = useCallback(() => isMutationPending.current, []);

  // ── useEffect: fires when nodes change after a triggerLayout call ─────────
  useEffect(() => {
    if (!isMutationPending.current) return;
    if (nodes.length === 0) return;

    const currentFingerprint = nodes.map(n => n.id).sort().join(',');

    if (currentFingerprint === prevNodeIdsRef.current) {
      console.log('🔔 POST-MUTATION-LAYOUT: Nodes unchanged, waiting for next render...');
      return;
    }

    console.log('🔔 POST-MUTATION-LAYOUT: Nodes updated, running dagre layout', {
      nodeCount: nodes.length,
      edgeCount: edges.length,
      preset: activePresetRef.current,
    });

    runLayout(nodes, edges, activePresetRef.current);

    isMutationPending.current = false;
    prevNodeIdsRef.current = currentFingerprint;

  }, [nodes, edges, runLayout]);

  // Keep prevNodeIdsRef in sync when not pending
  useEffect(() => {
    if (!isMutationPending.current) {
      prevNodeIdsRef.current = nodes.map(n => n.id).sort().join(',');
    }
  }, [nodes]);

  return { triggerLayout, relayout, isLayoutPending };
}
